import React from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import theme from './theme';
import { EFFICACY_TERMS } from './efficacyTerminology';

const DOT_CLEAN = '#059669';
const DOT_RESIDUAL = '#d97706';
const BANNER_BG = '#ecfdf5';
const BANNER_BORDER = 'rgba(5, 150, 105, 0.2)';

function SwabDot({ event, onDotClick }) {
  const color = event.status === 'residual' ? DOT_RESIDUAL : DOT_CLEAN;
  const title = `${event.specimenLabel} · ${event.location}`;
  const tip = (
    <Box sx={{ maxWidth: 280 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontSize: 11, mt: 0.5 }}>{event.category}</Typography>
      <Typography sx={{ fontSize: 11, mt: 0.5 }}>
        Before: {event.before} → After: {event.after}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tip} arrow placement="top">
      <Box
        component="button"
        type="button"
        onClick={() => onDotClick?.(event)}
        aria-label={`${event.status === 'residual' ? EFFICACY_TERMS.postUvcPositive.short : EFFICACY_TERMS.cleared.short} swab at ${event.location}`}
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: color,
          border: '2px solid #fff',
          boxShadow: '0 0 0 1px rgba(15,23,42,0.12)',
          flexShrink: 0,
          cursor: onDotClick ? 'pointer' : 'default',
          p: 0,
          '&:hover': onDotClick ? { transform: 'scale(1.15)' } : {},
        }}
      />
    </Tooltip>
  );
}

export default function SwabEvidenceTimeline({
  timelineWeeks,
  reportCount,
  monthLabel,
  streak,
  lastResidual,
  onDotClick,
}) {
  const showBanner = streak >= 2;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${theme.colors.borderDefault}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary }}>
            Swab evidence timeline
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.5, lineHeight: 1.5 }}>
            Each dot is an independent lab-verified swab event. Hover for surface and result.
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.colors.textSecondary, flexShrink: 0 }}>
          {reportCount > 0 ? `${reportCount} lab report${reportCount === 1 ? '' : 's'}` : 'No reports'}
          {monthLabel ? ` · ${monthLabel}` : ''}
        </Typography>
      </Box>

      {showBanner ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            mb: 2,
            borderRadius: `${theme.radius.md}px`,
            bgcolor: BANNER_BG,
            border: `1px solid ${BANNER_BORDER}`,
          }}
        >
          <ShieldOutlinedIcon sx={{ color: DOT_CLEAN, fontSize: 28, flexShrink: 0, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#14532d' }}>
              {streak} consecutive clean swab event{streak === 1 ? '' : 's'}
            </Typography>
            {lastResidual ? (
              <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.5, lineHeight: 1.5 }}>
                {EFFICACY_TERMS.postUvcPositive.timelineBannerLast(lastResidual.dateLabel, lastResidual.room)}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.5 }}>
                {EFFICACY_TERMS.postUvcPositive.timelineBannerNone}
              </Typography>
            )}
          </Box>
        </Box>
      ) : null}

      {timelineWeeks.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', bgcolor: theme.colors.bgSecondary, borderRadius: 2 }}>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
            No swab events in this month yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {timelineWeeks.map((week) => (
            <Box
              key={week.sortKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                py: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.colors.textSecondary,
                  minWidth: 72,
                  flexShrink: 0,
                }}
              >
                {week.weekLabel}
              </Typography>
              {week.empty ? (
                <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
                  no swabs scheduled
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {week.events.map((ev) => (
                    <SwabDot key={ev.id} event={ev} onDotClick={onDotClick} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2.5, pt: 2, borderTop: `1px solid ${theme.colors.borderDefault}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DOT_CLEAN }} />
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>
            {EFFICACY_TERMS.cleared.timelineLegend}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DOT_RESIDUAL }} />
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>
            {EFFICACY_TERMS.postUvcPositive.timelineLegend}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
